/**
 * ReportKit — shared building blocks for every AI report/brief in Resmo.
 * Mirrors the Candidates Store "Recruiter Playbook" aesthetic:
 * mono-caps colored section labels, dot-bullet lists, skill chips,
 * two-column grids, light sub-panels and soft callouts.
 */
import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export const RK = {
  navy: "#0F1B2D",
  orange: "#FF6A00",
  mustard: "#FFC145",
  red: "#B91C1C",
  green: "#0E5A4A",
  purple: "#7C3AED",
  amber: "#B45309",
  blue: "#1E40AF",
  slate: "#64748B",
  body: "#334155",
  sub: "#475569",
};

export function ReportLabel({ icon, color = RK.navy, className = "", children }) {
  return (
    <div
      className={`font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 ${className}`}
      style={{ color, fontWeight: 700 }}
    >
      {icon || null} · {children}
    </div>
  );
}

export function ReportList({
  title, icon, items, color = RK.navy, textColor = RK.body,
  ordered = false, renderItem, testId, className = "",
}) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId} className={className}>
      {title && <ReportLabel icon={icon} color={color} className="mb-1.5">{title}</ReportLabel>}
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li
            key={i}
            className={`text-[13px] leading-snug relative ${ordered ? "pl-6" : "pl-3"}`}
            style={{ color: textColor }}
          >
            {ordered ? (
              <span className="font-mono-rm text-[10px] absolute left-0 top-[2px]" style={{ color, fontWeight: 700 }}>
                {String(i + 1).padStart(2, "0")}
              </span>
            ) : (
              <span style={{ position: "absolute", left: 0, top: 6, width: 4, height: 4, borderRadius: "50%", background: color }} />
            )}
            {renderItem ? renderItem(it, i) : it}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReportRichList({
  title, icon, items, color = RK.navy, headingColor = RK.navy,
  numbered = false, testId, className = "",
}) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId} className={className}>
      {title && <ReportLabel icon={icon} color={color} className="mb-1.5">{title}</ReportLabel>}
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="pl-3 relative">
            <span style={{ position: "absolute", left: 0, top: 7, width: 4, height: 4, borderRadius: "50%", background: color }} />
            <div className="text-[13px] leading-snug font-semibold" style={{ color: headingColor }}>
              {numbered ? `${String(i + 1).padStart(2, "0")} · ` : ""}{it.heading}
            </div>
            {it.body && <p className="text-[12.5px] leading-snug mt-0.5" style={{ color: RK.sub }}>{it.body}</p>}
            {it.extra && <div className="text-[12px] leading-snug mt-1 font-semibold" style={{ color }}>{it.extra}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const CHIP_TONES = {
  primary:  { bg: "#FFF2EC", color: RK.navy, border: RK.orange, dot: RK.orange },
  nice:     { bg: "#F1F5F9", color: "#334155", border: "#CBD5E1", dot: "#94A3B8" },
  trending: { bg: "#FEF3C7", color: "#78350F", border: "#F59E0B", dot: "#F59E0B" },
  teal:     { bg: "#ECFDF5", color: "#064E3B", border: "#0E5A4A", dot: "#0E5A4A" },
};

export function ReportChip({ label, tone = "nice", dataTestId }) {
  const s = CHIP_TONES[tone] || CHIP_TONES.nice;
  return (
    <span
      data-testid={dataTestId}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono-rm uppercase tracking-widest px-2 py-1 rounded"
      style={{
        background: s.bg, color: s.color,
        border: `1.5px solid ${s.border}`,
        boxShadow: `2px 2px 0 0 ${s.dot}22`,
        fontWeight: 700,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot, flexShrink: 0 }} />
      {label}
    </span>
  );
}

export function ReportChips({ title, icon, items, tone = "nice", color = RK.navy, testId, className = "" }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId} className={className}>
      {title && <ReportLabel icon={icon} color={color} className="mb-1.5">{title}</ReportLabel>}
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <ReportChip key={i} label={typeof it === "string" ? it : it.name} tone={tone} />
        ))}
      </div>
    </div>
  );
}

export function ReportGrid({ cols = 2, children, className = "" }) {
  const grid = cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return <div className={`grid grid-cols-1 ${grid} gap-4 ${className}`}>{children}</div>;
}

export function ReportPanel({ title, icon, collapsible = false, defaultOpen = true, testId, children, className = "" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={`rounded-md overflow-hidden ${className}`}
      style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
      data-testid={testId}
    >
      {title && (
        collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center justify-between px-3 py-2"
            style={{ background: "#F1F5F9" }}
            data-testid={testId ? `${testId}-toggle` : undefined}
          >
            <ReportLabel icon={icon}>{title}</ReportLabel>
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        ) : (
          <div className="px-3 py-2" style={{ background: "#F1F5F9" }}>
            <ReportLabel icon={icon}>{title}</ReportLabel>
          </div>
        )
      )}
      {(!collapsible || open) && <div className="p-3 md:p-4">{children}</div>}
    </div>
  );
}

export function ReportCallout({
  title, icon, color = RK.amber, bg = "#FFFBEB", border = "#F59E0B",
  testId, children, className = "",
}) {
  return (
    <div className={`p-3 rounded-md ${className}`} style={{ background: bg, border: `1.5px solid ${border}` }} data-testid={testId}>
      {title && <ReportLabel icon={icon} color={color} className="mb-1">{title}</ReportLabel>}
      <div className="text-[13px] leading-snug" style={{ color: RK.body }}>{children}</div>
    </div>
  );
}

export function ReportStat({ label, icon, value, testId }) {
  if (!value) return null;
  return (
    <div className="p-2 rounded" style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }} data-testid={testId}>
      <div
        className="font-mono-rm text-[9px] uppercase tracking-widest inline-flex items-center gap-1 mb-0.5"
        style={{ color: RK.slate, fontWeight: 700 }}
      >
        {icon || null} {label}
      </div>
      <div className="text-xs font-bold" style={{ color: RK.navy }}>{value}</div>
    </div>
  );
}
